import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { BusinessPlanDocument } from "@/types/startup.types";
import {
  BUSINESS_PLAN_SECTION_KEYS,
  BUSINESS_PLAN_SECTION_LABELS,
} from "@/constants/business-plan-sections";
import { CHART_COLORS } from "@/constants/chart-colors";
import { SITE_LOGO_ALT } from "@/constants/site";
import { FlameLogo } from "@/components/atoms/FlameLogo";
import {
  buildInvestorHeroKpis,
  resolveInvestorHighlights,
} from "@/utils/business-plan-investor-metrics";
import { Button } from "@/components/atoms";
import { BusinessPlanMarkdown } from "@/components/business-plan/BusinessPlanMarkdown";
import "./business-plan-print.css";

const SECTION_LABELS = BUSINESS_PLAN_SECTION_LABELS;

const PIE_COLORS = [
  CHART_COLORS.revenue,
  CHART_COLORS.focus,
  CHART_COLORS.asset,
  CHART_COLORS.warn,
  CHART_COLORS.expense,
  CHART_COLORS.cashPositive,
];

function formatMoney(value: number, currency = "USD") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatMonthLabel(month: string) {
  const [year, mon] = month.split("-");
  const date = new Date(Number(year), Number(mon) - 1, 1);
  return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

export interface BusinessPlanDocumentViewProps {
  plan: BusinessPlanDocument;
  showPrintButton?: boolean;
  onPrint?: () => void;
}

export const BusinessPlanDocumentView: React.FC<BusinessPlanDocumentViewProps> = ({
  plan,
  showPrintButton = false,
  onPrint,
}) => {
  const snapshot = plan.financialSnapshot;
  const currency = snapshot?.period.currency ?? "USD";
  const companyName = plan.companyProfile?.name ?? "Business Plan";
  const tagline = plan.companyProfile?.tagline;

  const highlights = useMemo(
    () => (snapshot ? resolveInvestorHighlights(snapshot) : null),
    [snapshot],
  );

  const heroKpis = useMemo(
    () => (highlights ? buildInvestorHeroKpis(highlights, currency) : []),
    [highlights, currency],
  );

  const growthChartData = useMemo(
    () =>
      snapshot?.monthlyCashFlow.map((row) => ({
        label: formatMonthLabel(row.month),
        revenue: row.revenue,
        customers: row.customers,
        cash: row.cashBalance,
      })) ?? [],
    [snapshot?.monthlyCashFlow],
  );

  const scaleComparisonData = useMemo(() => {
    if (!highlights) return [];
    const rows = [
      { stage: "Today", revenue: highlights.current.revenue, customers: highlights.current.customers },
    ];
    if (highlights.at12Months) {
      rows.push({
        stage: "12 mo",
        revenue: highlights.at12Months.revenue,
        customers: highlights.at12Months.customers,
      });
    }
    rows.push({
      stage: highlights.horizonLabel,
      revenue: highlights.atHorizon.revenue,
      customers: highlights.atHorizon.customers,
    });
    return rows;
  }, [highlights]);

  const sectionKeys = BUSINESS_PLAN_SECTION_KEYS.filter(
    (key) => typeof plan[key as keyof BusinessPlanDocument] === "string",
  );

  const showRevenueMix =
    (snapshot?.revenueBreakdown.length ?? 0) > 1 ||
    (snapshot?.kpis.monthlyRevenue ?? 0) >= 1000;
  const showExpenseMix = (snapshot?.expenseBreakdown.length ?? 0) > 0;

  return (
    <div className="business-plan-document w-full min-w-0 space-y-6 px-3 py-6 sm:px-6 sm:py-8 lg:px-8">
      <header className="sh-card-elevated p-4 sm:p-6 lg:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-4">
            <FlameLogo size={44} className="mt-1" aria-label={SITE_LOGO_ALT} />
            <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand">
              Investor Business Plan
            </p>
            <h1 className="mt-2 text-2xl font-black text-white sm:text-3xl lg:text-4xl">{companyName}</h1>
            {tagline ? (
              <p className="mt-2 max-w-2xl text-sm text-white/65">{tagline}</p>
            ) : null}
            {plan.generatedAt ? (
              <p className="mt-4 text-xs text-white/45">
                Generated {new Date(plan.generatedAt).toLocaleString()}
                {snapshot?.period
                  ? ` · Growth model through ${new Date(snapshot.period.endDate).toLocaleDateString()}`
                  : ""}
              </p>
            ) : null}
            </div>
          </div>
          {showPrintButton && onPrint ? (
            <Button
              type="button"
              variant="primary"
              size="sm"
              className="bp-no-print"
              onClick={onPrint}
            >
              Export PDF
            </Button>
          ) : null}
        </div>

        {highlights && heroKpis.length > 0 ? (
          <div className="mt-8">
            <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.18em] text-brand">
              Investment highlights
            </p>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-3">
              {heroKpis.map((kpi) => (
                <div
                  key={kpi.label}
                  className={`rounded-[16px] p-4 ${
                    kpi.accent
                      ? "border border-brand/35 bg-brand/10"
                      : "sh-panel"
                  }`}
                >
                  <div className="text-[10px] font-bold uppercase tracking-wide text-white/45">
                    {kpi.label}
                  </div>
                  <div className="mt-1 text-lg font-black text-white sm:text-xl">{kpi.value}</div>
                  {kpi.sub ? (
                    <div className="mt-1 text-xs text-white/50">{kpi.sub}</div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </header>

      {snapshot && highlights ? (
        <section className="grid gap-6">
          <div className="sh-panel p-4 lg:col-span-2">
            <h2 className="sh-kicker mb-1">Revenue & customer growth trajectory</h2>
            <p className="mb-3 text-xs text-white/45">
              Forward-looking view from your burn chart model — the story investors evaluate.
            </p>
            <div className="h-56 sm:h-72 lg:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={growthChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: "rgba(255,255,255,0.55)" }} />
                  <YAxis
                    yAxisId="revenue"
                    tick={{ fontSize: 11, fill: "rgba(255,255,255,0.55)" }}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  />
                  <YAxis
                    yAxisId="customers"
                    orientation="right"
                    tick={{ fontSize: 11, fill: "rgba(255,255,255,0.55)" }}
                  />
                  <Tooltip
                    formatter={(value: number, name: string) =>
                      name === "Customers"
                        ? value.toLocaleString()
                        : formatMoney(value, currency)
                    }
                  />
                  <Legend />
                  <Bar
                    yAxisId="revenue"
                    dataKey="revenue"
                    fill={CHART_COLORS.revenue}
                    name="Monthly revenue"
                    radius={[4, 4, 0, 0]}
                    opacity={0.85}
                  />
                  <Line
                    yAxisId="customers"
                    type="monotone"
                    dataKey="customers"
                    stroke={CHART_COLORS.focus}
                    strokeWidth={2.5}
                    dot={false}
                    name="Customers"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="sh-panel p-4">
              <h2 className="sh-kicker mb-1">Scale path</h2>
              <p className="mb-3 text-xs text-white/45">Today vs projected milestones</p>
              <div className="h-48 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={scaleComparisonData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="stage" tick={{ fontSize: 11, fill: "rgba(255,255,255,0.55)" }} />
                    <YAxis tick={{ fontSize: 11, fill: "rgba(255,255,255,0.55)" }} />
                    <Tooltip formatter={(value: number) => formatMoney(value, currency)} />
                    <Legend />
                    <Bar dataKey="revenue" fill={CHART_COLORS.revenue} name="Monthly revenue" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="sh-panel p-4">
              <h2 className="sh-kicker mb-1">Cash position through plan</h2>
              <p className="mb-3 text-xs text-white/45">
                Runway and funding needs over the modeled period
              </p>
              <div className="h-48 sm:h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={growthChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "rgba(255,255,255,0.55)" }} />
                    <YAxis tick={{ fontSize: 11, fill: "rgba(255,255,255,0.55)" }} />
                    <Tooltip formatter={(value: number) => formatMoney(value, currency)} />
                    <Line
                      type="monotone"
                      dataKey="cash"
                      stroke={CHART_COLORS.cashPositive}
                      strokeWidth={2.5}
                      dot={false}
                      name="Cash balance"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {showRevenueMix || showExpenseMix ? (
            <div className="grid gap-6 lg:grid-cols-2">
              {showRevenueMix ? (
              <div className="sh-panel p-4">
                <h2 className="sh-kicker mb-3">Revenue mix (current)</h2>
                <div className="h-48 sm:h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={snapshot.revenueBreakdown}
                        dataKey="amount"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={(props) => {
                          const pct = Number(props.percent ?? 0) * 100;
                          return `${props.name} ${pct.toFixed(0)}%`;
                        }}
                      >
                        {snapshot.revenueBreakdown.map((_, index) => (
                          <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatMoney(value, currency)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              ) : null}

              {showExpenseMix ? (
              <div className={`sh-panel p-4 ${!showRevenueMix ? "lg:col-span-2" : ""}`}>
                <h2 className="sh-kicker mb-3">Operating cost structure</h2>
                <div className="h-48 sm:h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={snapshot.expenseBreakdown}
                        dataKey="amount"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={(props) => {
                          const pct = Number(props.percent ?? 0) * 100;
                          return `${props.name} ${pct.toFixed(0)}%`;
                        }}
                      >
                        {snapshot.expenseBreakdown.map((_, index) => (
                          <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatMoney(value, currency)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>
              ) : null}
            </div>
          ) : null}

          <div className="sh-card-elevated p-6">
            <h2 className="sh-section-title mb-2 border-b border-brand/30 pb-3">
              Investment & runway scenarios
            </h2>
            <p className="mb-4 text-sm text-white/55">
              Funding targets sized from your modeled burn and growth plan — aligned to how far
              you need to run to hit the projections above.
            </p>
            <table className="bp-funding-table">
              <thead>
                <tr>
                  <th>Scenario</th>
                  <th>Target runway</th>
                  <th>Suggested raise</th>
                </tr>
              </thead>
              <tbody>
                {snapshot.fundingAnalysis.scenarios.map((scenario) => (
                  <tr key={scenario.name}>
                    <td>
                      <div className="font-semibold text-white">{scenario.name}</div>
                      <div className="text-xs text-white/45">{scenario.description}</div>
                    </td>
                    <td className="text-white/75">{scenario.months} months</td>
                    <td className="font-semibold text-white">
                      {formatMoney(scenario.amount, currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-[14px] border border-white/10 bg-white/[0.02] p-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-white/40">
              Current operations (reference)
            </p>
            <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
              {[
                { label: "Cash on hand", value: formatMoney(highlights.current.cashBalance, currency) },
                { label: "Net burn", value: formatMoney(highlights.current.netBurn, currency) },
                {
                  label: "MRR today",
                  value: formatMoney(highlights.current.revenue, currency),
                },
                {
                  label: "Customers today",
                  value: highlights.current.customers.toLocaleString(),
                },
                {
                  label: "Runway",
                  value:
                    snapshot.kpis.runwayMonths == null
                      ? "Cash-flow positive"
                      : `${snapshot.kpis.runwayMonths} mo`,
                },
                {
                  label: "Break-even gap",
                  value: `${formatMoney(snapshot.kpis.monthlyRevenueGap, currency)}/mo`,
                },
              ].map((kpi) => (
                <div key={kpi.label} className="rounded-[12px] bg-white/[0.03] p-3">
                  <div className="text-[10px] uppercase tracking-wide text-white/40">{kpi.label}</div>
                  <div className="mt-1 text-sm font-semibold text-white/75">{kpi.value}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <div className="rounded-[14px] border border-brand/20 bg-brand/5 px-4 py-3 text-sm text-white/70">
        Growth charts and investment highlights are computed from your burn chart model for the
        configured plan period. Financial narrative sections (revenue, expenses, projections,
        funding, executive summary, and traction metrics) and the Team section are generated
        deterministically from your snapshot and team roster — not estimated by AI. Qualitative
        sections (market, product, roadmap, risks) may use AI. Market Opportunity may include
        AI-estimated industry figures.
      </div>

      {sectionKeys.map((key) => (
        <section key={key} className="bp-section sh-card-elevated p-6">
          <h2 className="sh-section-title mb-4 border-b border-brand/30 pb-3">
            {SECTION_LABELS[key]}
          </h2>
          <BusinessPlanMarkdown
            content={String(plan[key as keyof BusinessPlanDocument])}
            sectionLabel={SECTION_LABELS[key]}
          />
        </section>
      ))}
    </div>
  );
};

export function printBusinessPlan() {
  document.body.classList.add("business-plan-printing");
  window.print();
  window.setTimeout(() => {
    document.body.classList.remove("business-plan-printing");
  }, 500);
}
